import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { SectionHeader, CTABand } from "./Home";
import { LandRatingCard, SAMPLE_RATING } from "../components/LandRatingCard";

const REPORT_SECTIONS = [
  ["Terraa Score & Grade", "Your property's overall risk position out of 1000, with a grade from Platinum to High Risk. Critical document gaps automatically collapse the score below 300."],
  ["Category Breakdown", "How the property scores on title clarity, encumbrances, zoning, document completeness, environment, and investment potential — each weighted by importance."],
  ["Assessment Findings", "The expert's finding on every individual factor reviewed — the exact answers behind the score, in plain language."],
  ["Government Record Verification", "A table of records checked (7/12, 8A, property card, encumbrance search, mutation entries) with status and references."],
  ["Deal-Breaker Alerts", "If the Mother Deed or Encumbrance Certificate is missing or disputed, a red high-risk warning appears prominently on the report."],
  ["Examiner's Notes & Disclaimer", "Summary observations from the reviewing expert, plus the scope and limitations of the review."],
];

export default function SampleReportPage() {
  return (
    <div data-testid="sample-report-page">
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Sample Report"
            title="See exactly what you'll receive."
            sub="Below is an anonymized preview of the Terraa Land Rating card and the sections inside the full PDF verification report."
            className="mb-16"
          />
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-10">
              <LandRatingCard rating={SAMPLE_RATING} sample />
              <div className="relative overflow-hidden border border-[#C1A74A]/60" data-testid="sample-report-preview">
                <img
                  src="/sample-report.png"
                  alt="Blurred preview of a sample Terraa Score PDF report"
                  className="w-full h-auto blur-[5px] scale-[1.04] select-none pointer-events-none"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#113835]/70 flex flex-col items-center justify-end pb-10 px-8 text-center">
                  <div className="w-11 h-11 bg-[#113835] text-[#C1A74A] flex items-center justify-center mb-4">
                    <Lock size={18} strokeWidth={1.5} />
                  </div>
                  <p className="font-accent italic text-xl text-white drop-shadow">The full report is unlocked for verified properties.</p>
                </div>
              </div>
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#113835] mb-8">What each report section means</h2>
              <div className="space-y-7">
                {REPORT_SECTIONS.map(([t, d], i) => (
                  <div key={t} className="border-l border-[#C1A74A]/50 pl-6">
                    <p className="font-heading text-sm font-semibold text-[#113835]">
                      <span className="text-[#C1A74A] mr-2">0{i + 1}</span>{t}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 font-light leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
              <div className="mt-12 bg-[#F9FAFA] border border-[#113835]/10 p-8">
                <p className="font-heading text-sm font-medium text-[#113835]">Ready to see your property's real numbers?</p>
                <p className="mt-2 text-sm text-gray-600 font-light">Submit the property details and our experts will begin the review.</p>
                <Link to="/request-title-search" data-testid="sample-report-cta" className="tt-btn-gold mt-6 inline-flex">
                  Request Property Verification <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
